import { useEffect, useState } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import api from "../lib/api"
import { getErrorMessage } from "../lib/errors"
import type { DataTableSort } from "../components/ui/table/DataTable"

type UseServerTableOptions<T> = {
  endpoint: string
  defaultPageSize?: number
  defaultSort?: DataTableSort
  /** Filtros/búsqueda adicionales enviados como query params. Cambiar esta referencia reinicia a la página 1. */
  params?: Record<string, string | undefined>
  extractRows: (data: any) => T[]
  extractTotal: (data: any) => number
}

export function useServerTable<T>({
  endpoint,
  defaultPageSize = 20,
  defaultSort = null,
  params = {},
  extractRows,
  extractTotal,
}: UseServerTableOptions<T>) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSizeState] = useState(defaultPageSize)
  const [sort, setSort] = useState<DataTableSort>(defaultSort)

  const paramsKey = JSON.stringify(params)

  useEffect(() => {
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey])

  function setPageSize(size: number) {
    setPageSizeState(size)
    setPage(1)
  }

  const queryKey = ["server-table", endpoint, page, pageSize, sort?.column, sort?.dir, paramsKey]

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const queryParams: Record<string, string> = { page: String(page), limit: String(pageSize) }
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== "") queryParams[key] = value
      }
      if (sort) {
        queryParams.sortBy = sort.column
        queryParams.sortDir = sort.dir
      }
      const res = await api.get(endpoint, { params: queryParams })
      return res.data.data ?? res.data
    },
    placeholderData: keepPreviousData,
  })

  // isFetching (no isPending) para que "loading" se reactive en cada refetch/página/filtro,
  // no solo en el montaje inicial — keepPreviousData deja isPending en false salvo la primera carga.
  // refetchOnWindowFocus está deshabilitado globalmente (createQueryClient), así que esto no
  // reactiva el ícono de recarga por focus de ventana en background.

  const payload = query.data
  const rows = payload ? extractRows(payload) : []
  const total = payload ? extractTotal(payload) : 0

  return {
    rows,
    total,
    /** Respuesta cruda del servidor, para leer campos extra como `summary` (totales globales de las métricas). */
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading: query.isFetching,
    error: query.isError ? getErrorMessage(query.error) : "",
    reload: async () => {
      await query.refetch()
    },
    /** Current cache key for this page/sort/filter combo — for optimistic updates via applyOptimistic/rollbackOptimistic. */
    queryKey,
  }
}
